import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { useLocation } from 'react-router-dom';
import './Navbar.css';

export default function Navbar({ theme, toggleTheme }) {
  const [hidden, setHidden] = useState(false);
  const [resumeNear, setResumeNear] = useState(false);
  const [aboutNear, setAboutNear] = useState(false);
  const [themeNear, setThemeNear] = useState(false);
  const lastScrollY = useRef(0);
  const resumeRef = useRef(null);
  const aboutRef = useRef(null);
  const themeRef = useRef(null);
  const location = useLocation();
  const isAboutPage = location.pathname.includes('/about');

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      // Scrolling down → hide, scrolling up → show
      if (currentY > lastScrollY.current && currentY > 80) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let wasNear = false;
    let wasAboutNear = false;
    let audioCtx = null;
    let clickBuffer = null;

    const initAudio = async () => {
      if (!audioCtx) {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        try {
          const r = await fetch(process.env.PUBLIC_URL + '/splat.wav');
          const buf = await r.arrayBuffer();
          clickBuffer = await audioCtx.decodeAudioData(buf);
        } catch (e) {}
      }
    };
    window.addEventListener('click', initAudio, { once: true });

    const playClick = () => {
      if (audioCtx && clickBuffer) {
        const source = audioCtx.createBufferSource();
        source.buffer = clickBuffer;
        source.playbackRate.value = 1.8;
        const gain = audioCtx.createGain();
        gain.gain.value = 0.3;
        source.connect(gain);
        gain.connect(audioCtx.destination);
        source.start(0);
      }
    };

    const handleMouseMove = (e) => {
      if (resumeRef.current) {
        const rect = resumeRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.sqrt((e.clientX - cx) ** 2 + (e.clientY - cy) ** 2);
        const isNear = dist < 70;
        if (isNear && !wasNear) playClick();
        wasNear = isNear;
        setResumeNear(isNear);
      }
      if (aboutRef.current) {
        const rect = aboutRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.sqrt((e.clientX - cx) ** 2 + (e.clientY - cy) ** 2);
        const isNear = dist < 70;
        if (isNear && !wasAboutNear) playClick();
        wasAboutNear = isNear;
        setAboutNear(isNear);
      }
      if (themeRef.current) {
        const rect = themeRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.sqrt((e.clientX - cx) ** 2 + (e.clientY - cy) ** 2);
        setThemeNear(dist < 70);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', initAudio);
    };
  }, []);

  return (
    <motion.nav
      className={`navbar ${hidden ? 'navbar-hidden' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.42, 0, 0.58, 1], delay: 0.2 }}
    >
      <div className="nav-container">
        <aside className="nav-wrapper">
          <a href={process.env.PUBLIC_URL + '/'} className="nav-logo">
            <div className="logo-block">
              <img
                loading="lazy"
                width="75"
                alt=""
                src="https://cdn.prod.website-files.com/62a9c118130e8b4c6d766b99/64ca14b0fe3deb1da9fcaf78_raunak.png"
                className="logo-image"
              />
            </div>
          </a>
          <div className="nav-right">
            <button
              className={`theme-toggle ${themeNear ? 'theme-toggle-near' : ''}`}
              onClick={toggleTheme}
              ref={themeRef}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                <svg className="moon-icon" width="18" height="18" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <defs>
                    <clipPath id="moon-fill">
                      <rect className="moon-fill-rect" x="0" y="24" width="24" height="24" />
                    </clipPath>
                  </defs>
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" fill="none" />
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" fill="currentColor" clipPath="url(#moon-fill)" />
                </svg>
              )}
            </button>
            {!isAboutPage && (
            <a
              href={process.env.PUBLIC_URL + '/about'}
              className={`nav-link ${aboutNear ? 'nav-link-near' : ''}`}
              ref={aboutRef}
            >
              <div className="logo-block">
                <span className="nav-link-text">About
                  <svg className="about-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
              </div>
            </a>
            )}
            <a
              href={process.env.PUBLIC_URL + '/Personal/UXD_Resume_RaunakAgarwal.pdf'}
              target="_blank"
              rel="noreferrer"
              className={`nav-link ${resumeNear ? 'nav-link-near' : ''}`}
              ref={resumeRef}
            >
              <div className="logo-block">
                <span className="nav-link-text">Resume
                  <svg className="external-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="7" y1="17" x2="17" y2="7" />
                    <polyline points="7 7 17 7 17 17" />
                  </svg>
                </span>
              </div>
            </a>
          </div>
        </aside>
      </div>
    </motion.nav>
  );
}
